import { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { toast, Toaster } from "sonner";
import { Loader2, AlertTriangle, CheckCircle2, MapPin, Zap } from "lucide-react";

export default function OnSite() {
  const [step, setStep] = useState(1);
  const [cfg, setCfg] = useState(null);
  const [f, setF] = useState({
    customer_name: "", phone: "", email: "", company: "",
    address: "", lat: 0, lng: 0, distance_miles: 0,
    equipment_description: "", problem_description: "", is_emergency: false,
  });
  const [quote, setQuote] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/config").then(r => setCfg(r.data)); }, []);

  const upd = (k, v) => setF(p => ({ ...p, [k]: v }));

  const checkAddress = async () => {
    if (!f.address.trim()) { toast.error("Enter an address"); return; }
    setBusy(true);
    try {
      const r = await api.post("/onsite/quote", { address: f.address });
      setQuote(r.data);
      setF(p => ({ ...p, lat: r.data.lat, lng: r.data.lng, distance_miles: r.data.distance_miles }));
      if (r.data.within_service_area) setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not check address");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/onsite/checkout", { ...f, origin_url: window.location.origin });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  return (
    <Layout>
      <Toaster theme="dark" />
      <section className="mx-auto max-w-3xl px-4 sm:px-8 py-12">
        <div className="eyebrow mb-2">On-Site Service</div>
        <h1 className="hero-title text-4xl sm:text-5xl text-white">We'll come to your equipment.</h1>

        {cfg && (
          <div className="mt-6 grid sm:grid-cols-3 gap-3">
            <StatBox label="Rate" value={`$${cfg.labor_rate}/hr`} />
            <StatBox label="Minimum" value={`${cfg.min_hours} hours`} />
            <StatBox label="Deposit" value={`$${cfg.deposit_amount}`} accent />
          </div>
        )}

        <div className="mt-6 shop-card p-5 border-l-4 border-red-500">
          <div className="text-slate-300 text-sm">
            <strong className="text-white">Terms:</strong> $75/hr with a 2-hour minimum ($150 minimum bill).
            A <span className="text-red-500 font-semibold">$150 deposit</span> holds your slot and is credited toward the invoice.
            <span className="block mt-2 text-slate-400">Deposit is non-refundable unless Childress Hydraulics cancels. Service radius: 50 miles from Greensburg, KY 42743.</span>
          </div>
        </div>

        {step === 1 && (
          <div className="mt-8 shop-card p-6 space-y-4">
            <div>
              <div className="eyebrow mb-2">Job Address *</div>
              <input data-testid="onsite-address" value={f.address} onChange={e => upd("address", e.target.value)}
                placeholder="123 Farm Rd, Greensburg, KY 42743" className="input" />
              <div className="text-slate-500 text-xs mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> We'll check the distance from our shop.</div>
            </div>
            <button onClick={checkAddress} disabled={busy} data-testid="onsite-check-address"
              className="btn-red px-6 py-4 rounded-sm inline-flex items-center gap-2">
              {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Check Service Area
            </button>

            {quote && !quote.within_service_area && (
              <div className="mt-4 p-4 rounded-sm bg-amber-500/10 border border-amber-500/50 flex gap-3">
                <AlertTriangle className="w-6 h-6 text-amber-400 flex-shrink-0" />
                <div>
                  <div className="font-display font-bold text-amber-300 text-lg">Out of service area</div>
                  <div className="text-slate-300 text-sm">
                    That address is <strong>{quote.distance_miles} miles</strong> out (max 50).
                    Please <a href="tel:2704054083" className="text-red-500 underline">call 270-405-4083</a> — Dylan can advise on drop-off or referrals.
                  </div>
                </div>
              </div>
            )}
            {quote && quote.within_service_area && (
              <div className="mt-4 p-4 rounded-sm bg-green-500/10 border border-green-500/50 flex gap-3">
                <CheckCircle2 className="w-6 h-6 text-green-400" />
                <div className="text-slate-200 text-sm"><strong>{quote.distance_miles} miles</strong> — you're in range. Continue below.</div>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="mt-8 space-y-6">
            <div className="shop-card p-5">
              <div className="eyebrow mb-3">Your Info</div>
              <div className="grid sm:grid-cols-2 gap-4">
                <F label="Name *"><input required data-testid="onsite-name" value={f.customer_name} onChange={e => upd("customer_name", e.target.value)} className="input" /></F>
                <F label="Phone *"><input required data-testid="onsite-phone" type="tel" value={f.phone} onChange={e => upd("phone", e.target.value)} className="input" /></F>
                <F label="Email *"><input required data-testid="onsite-email" type="email" value={f.email} onChange={e => upd("email", e.target.value)} className="input" /></F>
                <F label="Company (opt)"><input data-testid="onsite-company" value={f.company} onChange={e => upd("company", e.target.value)} className="input" /></F>
              </div>
            </div>

            <div className="shop-card p-5">
              <div className="eyebrow mb-3">Equipment & Problem</div>
              <F label="Equipment *"><input required data-testid="onsite-equipment" value={f.equipment_description} onChange={e => upd("equipment_description", e.target.value)} placeholder="John Deere 6410, boom cylinder" className="input" /></F>
              <div className="mt-3">
                <F label="What's Wrong? *"><textarea required data-testid="onsite-problem" value={f.problem_description} onChange={e => upd("problem_description", e.target.value)} placeholder="Leaking from rod seal, won't lift…" className="input min-h-[100px]" /></F>
              </div>
            </div>

            <label className={`shop-card p-5 flex items-start gap-3 cursor-pointer ${f.is_emergency ? "border-red-500" : ""}`}>
              <input type="checkbox" data-testid="onsite-emergency" checked={f.is_emergency} onChange={e => upd("is_emergency", e.target.checked)} className="mt-1 w-5 h-5 accent-red-600" />
              <div>
                <div className="font-display font-bold text-white uppercase flex items-center gap-2"><Zap className="w-4 h-4 text-amber-400" /> After-Hours Emergency</div>
                <div className="text-slate-400 text-sm mt-1">Dylan gets an immediate alert. Emergency callouts may carry additional charges.</div>
              </div>
            </label>

            <div className="shop-card p-5 bg-gradient-to-b from-red-600/5 to-transparent">
              <div className="flex items-center justify-between">
                <div>
                  <div className="eyebrow">Deposit Due Now</div>
                  <div className="font-display font-black text-5xl text-white">$150.00</div>
                  <div className="text-slate-400 text-sm mt-1">Non-refundable unless we cancel. Credited to invoice.</div>
                </div>
              </div>
              <button type="submit" disabled={busy} data-testid="onsite-submit"
                className="btn-red mt-5 px-8 py-5 rounded-sm w-full inline-flex items-center justify-center gap-2 text-lg">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Pay Deposit & Schedule
              </button>
              <div className="text-slate-500 text-[11px] font-mono-tech mt-2 text-center">Powered by Stripe • Test card 4242 4242 4242 4242</div>
            </div>
          </form>
        )}
      </section>
    </Layout>
  );
}

function StatBox({ label, value, accent }) {
  return (
    <div className={`shop-card p-4 ${accent ? "border-red-500" : ""}`}>
      <div className="eyebrow">{label}</div>
      <div className={`font-display font-black text-2xl ${accent ? "text-red-500" : "text-white"}`}>{value}</div>
    </div>
  );
}
function F({ label, children }) { return <label className="block"><div className="eyebrow mb-1">{label}</div>{children}</label>; }
